import API from "../Services/API";
import Model from "./Model";
import State from "./State";
import { useSimpleWrapper } from "./SimpleWrapper";

const useBroadcasts = (alwaysRefresh, archived = false) => {
    return Model({
        alwaysRefresh,
        stateKey: ['broadcasts', archived],
        fetchData: () => API.get(`/broadcasts?archived=${archived}`),
        returnDataKey: 'broadcasts',
        saveAdditionalData: (response) => {
            response.map((broadcast) => {
                State.putIfNotExists(['broadcast', broadcast.id], broadcast);
                return broadcast;
            });
        },
        registerAdditionalMethods: (opts) => {
            return {
                addBroadcast: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/broadcasts', data),
                        null,
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Failed to load broadcasts.',
    });
}


const useBroadcast = (broadcastId, alwaysRefresh) => {
    return Model({
        alwaysRefresh,
        stateKey: ['broadcast', broadcastId],
        fetchData: () => API.get(`/broadcasts/${broadcastId}`),
        returnDataKey: 'broadcast',
        subscribeToUpdates: {
            entity: {entityModel: 'Broadcasts/Broadcast', entityId: broadcastId},
            handler: (data, opts) => {
                console.log('Broadcast updated', data);
                opts.refresh();
            },
        },
        registerAdditionalMethods: (opts) => {
            return {
                update: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/broadcasts/'+broadcastId, data),
                        null,
                        true,
                    );
                },
                selectRecipients: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/broadcasts/${broadcastId}/recipients`, data),
                        null,
                        true,
                    );
                },
                removeRecipients: (recipientIds) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/broadcasts/${broadcastId}/recipients`, {recipientIds}),
                        null,
                        true,
                    );
                },
                send: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/broadcasts/${broadcastId}/send`, data),
                        null,
                        true,
                    );
                },
                duplicate: (duplicateId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/broadcasts/${broadcastId}/duplicate`, {duplicateId}),
                        null,
                        true,
                    );
                },
                archive: () => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/broadcasts/${broadcastId}`),
                        null,
                        true,
                    );
                },
                restore: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/broadcasts/${broadcastId}/restore`),
                        null,
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Failed to load broadcast.',
    });
}

const broadcastIcons = {
    'Draft': 'Pencil',
    'Ready': 'PlayPause',
    'Scheduled': 'Click',
    'Sending': 'Loading',
    'Sent': 'Check',
    'Archived': 'Archive',
}

const recipientStatuses = {
    'Inactive': {
        icon: 'Ban',
        type: 'Unavailable',
        description: 'The customer does not have an active jobs',
    },
    'Unreachable': {
        icon: 'SquareQuestion',
        type: 'Unavailable',
        description: 'The customer can\'t be reached using the selected channels',
    },
    'Opted-Out': {
        icon: 'Stop',
        type: 'Unavailable',
        description: 'The customer has opted out of receiving promotional messages',
    },
    'Ready': {
        icon: 'PlayPause',
        type: 'Pending',
        description: 'The customer is ready to receive the message',
    },
    'Sending': {
        icon: 'Play',
        type: 'Pending',
        description: 'The message is being sent to the customer',
    },
    'Sent': {
        icon: 'Check',
        type: 'Successful',
        description: 'The message has been sent to the customer',
    },
    'Failed': {
        icon: 'XMark',
        type: 'Error',
        description: 'The message failed to send to the customer',
    },
    'Delivered': {
        icon: 'CheckDouble',
        type: 'Successful',
        description: 'The message has been delivered to the customer',
    },
    'Opened': {
        icon: 'EnvelopeOpen',
        type: 'Successful',
        description: 'The message has been opened by the customer',
    },
    'Clicked': {
        icon: 'HandPointer',
        type: 'Successful',
        description: 'The customer has clicked a link in the message',
    },
    'Unsubscribed': {
        icon: 'Ban',
        type: 'Error',
        description: 'The customer has unsubscribed from further messages',
    },
    'Complained': {
        icon: 'Ban',
        type: 'Error',
        description: 'The customer has complained about the message (reported as spam)',
    },
    'Unknown': {
        icon: 'Question',
        type: 'Error',
        description: 'The status of the message is unknown',
    },
}

const typeClasses = {
    'Unavailable': {
        border: 'border-amber-600 dark:border-amber-700',
        bgLight: 'bg-amber-50 dark:bg-amber-900',
        bg: 'bg-amber-600 dark:bg-amber-700',
        fg: 'text-amber-600 dark:text-amber-700 fill-amber-600 dark:fill-amber-700',
    },
    'Pending': {
        border: 'border-brand',
        bgLight: 'bg-slate-50 dark:bg-slate-900',
        bg: 'bg-brand',
        fg: 'text-brand fill-brand',
    },
    'Successful': {
        border: 'border-green-600 dark:border-green-700',
        bgLight: 'bg-green-50 dark:bg-green-900',
        bg: 'bg-green-600 dark:bg-green-700',
        fg: 'text-green-600 dark:text-green-700 fill-green-600 dark:fill-green-700',
    },
    'Error': {
        border: 'border-red-600 dark:border-red-700',
        bgLight: 'bg-red-50 dark:bg-red-900',
        bg: 'bg-red-600 dark:bg-red-700',
        fg: 'text-red-600 dark:text-red-700 fill-red-600 dark:fill-red-700',
    },
    'Unknown': {
        border: 'border-gray-600 dark:border-gray-700',
        bgLight: 'bg-gray-50 dark:bg-gray-900',
        bg: 'bg-gray-600 dark:bg-gray-700',
        fg: 'text-gray-600 dark:text-gray-700 fill-gray-600 dark:fill-gray-700',
    },

}

const groupStatsByType = (stats) => {
    return Object.keys(stats).reduce((acc, key) => {
        const type = recipientStatuses[key]?.type ?? 'Unknown';
        acc[type] = (acc[type] ?? 0) + stats[key];
        return acc;
    }, {});
}

const useAddCustomersToBroadcast = () => {
    return useSimpleWrapper(
        ({ broadcastId, customerIds }) => API.post(`/broadcasts/${broadcastId}/customers`, { customerIds }),
        'Customers added to broadcast',
        'Failed to add customers to broadcast',
    )
}

export {
    useBroadcasts,
    useBroadcast,
    broadcastIcons,
    recipientStatuses,
    groupStatsByType,
    typeClasses,
    useAddCustomersToBroadcast,
}
